// Landing page at "/" — the first surface a new visitor sees. Designed as a
// full-width marketing page (NOT inside the QuizLayout card) with multiple
// sections covering hero, methodology, social proof, comparison, coaches,
// FAQ, and a closing CTA. The funnel narrows to a single button — "Get
// started" — which drops the user into the quiz at QUIZ.entryQuestionId.

import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useQuizStore } from '../store/quizStore';
import { flushQueue } from '../lib/api';
import { track, trackQuizStart } from '../lib/analytics';
import { QUIZ } from '../data/quiz.generated';
import { REVIEWS, REVIEW_STATS } from '../data/reviews';
import { COACHES } from '../data/coaches';

const HOW_IT_WORKS: { n: string; title: string; body: string }[] = [
  {
    n: '01',
    title: 'Answer the assessment',
    body: 'Direct questions about what you do, what you like, and what trade-offs you can live with. No filler.',
  },
  {
    n: '02',
    title: 'Get your personalized report',
    body: 'Your six CAPBOI scores, your dominant archetype, ten career matches with real salary tiers and BLS growth, and a 90-day plan calibrated to your answers.',
  },
  {
    n: '03',
    title: 'Take the next step with a coach',
    body: 'Weekly group sessions or a 1:1 with a vetted coach to walk through the report and turn it into actual moves.',
  },
];

const TYPES: { name: string; what: string }[] = [
  {
    name: 'Personality',
    what: 'How you think, decide, and work — useful for picking environments that fit instead of fight you.',
  },
  {
    name: 'Aptitude',
    what: 'Where your raw skills already line up. Less useful in isolation; pair it with interest and values.',
  },
  {
    name: 'Interest',
    what: 'What energizes you when nobody is making you do it. Strong predictor of staying power in a role.',
  },
  {
    name: 'Values',
    what: 'What you refuse to trade off — money, flexibility, meaning, ambition. The hidden driver of regret.',
  },
];

const AUDIENCES: { label: string; body: string }[] = [
  {
    label: 'Career changers',
    body: "You know your current path isn't it. The hard part is figuring out what is — without burning a year on the wrong pivot.",
  },
  {
    label: 'Recent grads',
    body: 'You finished school with no obvious direction. This gives you a credible starting point grounded in who you actually are.',
  },
  {
    label: 'Returning to work',
    body: "After a break — kids, illness, a layoff — your old role may not fit anymore. We map what you've gained, not just where you stopped.",
  },
  {
    label: 'Mid-career, restless',
    body: "Stable but bored. We surface the adjacent moves where your existing skills compound, not the ones that ask you to start from zero.",
  },
];

const COMPARISON: { feature: string; us: string; them: string }[] = [
  { feature: 'Personality model', us: 'Modern 6-dimension CAPBOI', them: '40-year-old Holland codes' },
  { feature: 'Career list length', us: '10 real, ranked matches', them: '3 generic categories' },
  { feature: 'Salary tiers', us: 'Entry / mid / senior per role', them: 'Vague ranges or none' },
  { feature: 'Job growth data', us: 'Real BLS 10-year projections', them: 'AI guesses' },
  { feature: 'Strategy plan', us: '90-day phased + year-1 roadmap', them: 'None' },
  { feature: 'Live coaching', us: '4 hours/month + 1:1 add-on', them: 'PDF + farewell' },
];

const FAQ: { q: string; a: string }[] = [
  {
    q: 'How long does the assessment take?',
    a: 'Most people finish in under 20 minutes. Take longer if you want — there\'s no benefit to rushing.',
  },
  {
    q: 'How is this different from a free Myers-Briggs quiz?',
    a: 'Myers-Briggs gives you a 4-letter type and a one-paragraph blurb. Our report is built from a 6-dimension CAPBOI personality model plus your answers about life context, salary, and work style — and outputs ten ranked career matches, real salary tiers, BLS growth data, and a 90-day plan. The free quizzes do the first 5% of the job.',
  },
  {
    q: 'Will the test tell me what I should do for work?',
    a: 'It gives you a shortlist of careers that fit your profile and explains why. The decision is still yours — but you start with a real shortlist instead of a blank page.',
  },
  {
    q: 'Is the assessment refundable?',
    a: 'Yes — 30-day money-back guarantee. If the report doesn\'t help you see your career more clearly, email us and we refund in full.',
  },
  {
    q: "What if I'm already in a senior role?",
    a: "The report works just as well for advancement — top matches and the 90-day plan calibrate to whether you're switching, advancing, or returning. Many users buy it to validate their next move before they make it.",
  },
  {
    q: 'How long until I see my report?',
    a: 'Your report generates within about 60 seconds of finishing the assessment. The dashboard unlocks immediately after checkout.',
  },
];

const HERO_IMAGES: { src: string; fallback?: string; alt: string }[] = [
  { src: '/hero/p1.avif', alt: 'Career coach and client reviewing a plan on a laptop' },
  { src: '/hero/p2.jpg', alt: 'Two professionals celebrating a win' },
  { src: '/hero/p4.jpg', alt: 'Coaching session in a creative workspace' },
  { src: '/hero/p5.avif', alt: 'Team reviewing career strategy together' },
];

export function Intro() {
  const navigate = useNavigate();
  const ensureSession = useQuizStore((s) => s.ensureSession);
  const currentQuestionId = useQuizStore((s) => s.currentQuestionId);
  const leadId = useQuizStore((s) => s.leadId);
  const markQuizStarted = useQuizStore((s) => s.markQuizStarted);

  useEffect(() => {
    void flushQueue();
  }, []);

  const handleStart = () => {
    ensureSession();
    markQuizStarted();
    const target = currentQuestionId ?? QUIZ.entryQuestionId;
    track('quiz_started', { resume: !!currentQuestionId });
    // jobtest.org GTM doc-specified event.
    trackQuizStart();
    navigate(`/q/${target}`);
  };

  const ctaLabel = leadId
    ? 'Continue with a fresh assessment →'
    : currentQuestionId
      ? 'Pick up where you left off →'
      : 'Find Your Calling →';

  return (
    <div className="min-h-dvh flex flex-col bg-canvas text-ink">
      <TopHeader showResumeNote={!!currentQuestionId} onStart={handleStart} />
      <main className="flex-1">
        <Hero ctaLabel={ctaLabel} onStart={handleStart} />
        <HowItWorks onStart={handleStart} />
        <WhyThisTest onStart={handleStart} />
        <TypesOfTests onStart={handleStart} />
        <WhoFor onStart={handleStart} />
        <Comparison onStart={handleStart} />
        <ImpactStats onStart={handleStart} />
        <Testimonials />
        <CoachesPreview onStart={handleStart} />
        <FAQSection />
        <FinalCTA ctaLabel={ctaLabel} onStart={handleStart} />
      </main>
      <PageFooter />
    </div>
  );
}

// ─── Top header ──────────────────────────────────────────────

function TopHeader({ showResumeNote, onStart }: { showResumeNote: boolean; onStart: () => void }) {
  return (
    <header className="sticky top-0 z-30 bg-canvas/85 backdrop-blur-xl border-b border-ink/10">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-3">
        <a href="/" className="flex-shrink-0" aria-label="Jobtest.org">
          <img src="/jobtestlogo.svg" alt="Jobtest.org" className="h-7 w-auto" width={319} height={76} />
        </a>
        <div className="flex items-center gap-3 sm:gap-5">
          {showResumeNote && (
            <span className="hidden md:inline text-xs font-bold uppercase tracking-wider text-teal-600">
              You have a session in progress
            </span>
          )}
          <a
            href="/login"
            className="hidden sm:inline text-sm font-bold text-ink hover:text-brand-700 transition"
          >
            Sign in
          </a>
          <button
            type="button"
            onClick={onStart}
            className="inline-flex items-center rounded-cta border-2 border-ink bg-brand text-ink font-extrabold text-sm px-4 py-2 sm:px-5 sm:py-2.5 shadow-[0_3px_0_0_#101828] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[0_1px_0_0_#101828] transition"
          >
            Take the Test →
          </button>
        </div>
      </div>
    </header>
  );
}

// ─── Hero ────────────────────────────────────────────────────

// The featured hero testimonial — mirrors the one on jobtest.org/testing.
const HERO_TESTIMONIAL = {
  rating: 5,
  title: 'A superb tool',
  body: 'In-depth analysis of my personality, preferences, and goals. The results excited me, and I would recommend this to a friend or family member exploring different career paths. If you seek more alignment in your life, this could be a valuable tool.',
  author: 'Dylan, M.',
};

function Hero({ ctaLabel, onStart }: { ctaLabel: string; onStart: () => void }) {
  return (
    <section className="max-w-[1200px] mx-auto px-4 sm:px-8 pt-8 sm:pt-14 pb-12 sm:pb-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-8 lg:gap-x-12 gap-y-8 items-start">
        {/* Text + CTA + rating + testimonial. order-1 everywhere. */}
        <div className="lg:col-span-6 order-1">
          <h1 className="text-display-md sm:text-display-lg text-ink leading-[1.05] mb-5 text-balance">
            Find a career that lights your fire.
          </h1>
          <p className="text-ink-soft text-lg sm:text-xl mb-7 max-w-xl">
            Navigate to career satisfaction. Our test will map your path to a fulfilling career.
          </p>
          {/* Full-width (widened + centered) on mobile; auto-width inline on desktop. */}
          <button type="button" className="btn-primary !w-full sm:!w-auto" onClick={onStart}>
            {ctaLabel}
          </button>
          <div className="flex items-center gap-3 mt-5">
            <AvatarStack />
            <div>
              <p className="text-sm font-bold text-ink flex items-center gap-1.5">
                <span aria-hidden className="text-brand">★ ★ ★ ★ ★</span>
                <span className="tabular-nums">4.9</span>
              </p>
              <p className="text-xs text-ink-soft">
                Over <span className="font-bold text-ink">300,000</span> users have found their calling
              </p>
            </div>
          </div>
          {/* Desktop: testimonial lives in the left column under the rating. */}
          <div className="hidden lg:block">
            <HeroTestimonial />
          </div>
        </div>

        {/* Featured-in logos. Mobile: right after the rating (order-2), i.e.
            ABOVE the testimonial + image. Desktop: full-width strip below the
            hero (order-4, col-span-12). Matches jobtest.org/testing. */}
        <div className="lg:col-span-12 order-2 lg:order-4 border-t border-ink/10 pt-7 lg:pt-9 mt-1">
          <p className="eyebrow text-center mb-4">As featured in</p>
          <LogoMarquee />
        </div>

        {/* Mobile-only testimonial — sits between the logos and the image. */}
        <div className="order-3 lg:hidden">
          <HeroTestimonial />
        </div>

        {/* Hero image grid. Mobile: LAST (order-4). Desktop: right of the
            text (order-2). */}
        <div className="lg:col-span-6 order-4 lg:order-2">
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {HERO_IMAGES.map((img) => (
              <div
                key={img.src}
                className="aspect-square rounded-2xl sm:rounded-3xl overflow-hidden border-2 border-ink/10 bg-canvas-subtle"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// Prominent single testimonial inside the hero (mirrors jobtest.org/testing).
function HeroTestimonial() {
  const t = HERO_TESTIMONIAL;
  return (
    <figure className="mt-7 rounded-2xl border-2 border-ink/10 bg-white p-5 max-w-xl">
      <div className="flex items-center gap-1 mb-2" aria-label={`${t.rating} out of 5 stars`}>
        {Array.from({ length: t.rating }).map((_, i) => (
          <span key={i} aria-hidden className="text-brand">★</span>
        ))}
      </div>
      <p className="text-sm font-extrabold text-ink mb-1.5">{t.title}</p>
      <blockquote className="text-sm sm:text-base text-ink-soft leading-relaxed mb-3 text-pretty">
        “{t.body}”
      </blockquote>
      <figcaption className="text-xs font-semibold text-ink">
        {t.author}
        <span className="text-ink-light font-normal"> · Verified</span>
      </figcaption>
    </figure>
  );
}

function AvatarStack() {
  return (
    <div className="flex -space-x-2 flex-shrink-0">
      {COACHES.slice(0, 4).map((c) => (
        <img
          key={c.id}
          src={c.imageSrc}
          alt=""
          className="w-8 h-8 rounded-full border-2 border-canvas object-cover"
          loading="lazy"
          width={32}
          height={32}
        />
      ))}
    </div>
  );
}

// ─── Featured in (press logo marquee) ────────────────────────

// JobTest's own brand-press logos, served from the same Webflow CDN as
// jobtest.org/testing. Rendered as an infinite marquee that pauses for users
// who prefer reduced motion (falls back to a centered wrapped row).
const FEATURED_LOGOS: { name: string; src: string }[] = [
  { name: 'NBC', src: 'https://cdn.prod.website-files.com/63b58014b3cc40403185e35e/65819c1ffcf8ca3e381e7c20_nbc.svg' },
  { name: 'Fox', src: 'https://cdn.prod.website-files.com/63b58014b3cc40403185e35e/65819c12414085c8cb4d7d49_fox.svg' },
  { name: 'CBS', src: 'https://cdn.prod.website-files.com/63b58014b3cc40403185e35e/65819bef7d0ec2566e37afc5_cbs.svg' },
  { name: 'ABC', src: 'https://cdn.prod.website-files.com/63b58014b3cc40403185e35e/65819bdf2dbf4e270ebade91_abc.svg' },
  { name: 'PACE', src: 'https://cdn.prod.website-files.com/63b58014b3cc40403185e35e/65819c2c3a3a4b1eaf3c6585_pace.svg' },
  { name: 'CUNY', src: 'https://cdn.prod.website-files.com/63b58014b3cc40403185e35e/65819bfb74bc333c5b325946_cuny.svg' },
  { name: 'Digital Journal', src: 'https://cdn.prod.website-files.com/63b58014b3cc40403185e35e/65819c08380a269498642611_diigtaljournal.svg' },
];

function LogoMarquee() {
  // Duplicate the list so the -50% translate loops seamlessly.
  const loop = [...FEATURED_LOGOS, ...FEATURED_LOGOS];
  return (
    <div
      className="relative overflow-hidden"
      style={{
        maskImage: 'linear-gradient(to right, transparent, #000 8%, #000 92%, transparent)',
        WebkitMaskImage: 'linear-gradient(to right, transparent, #000 8%, #000 92%, transparent)',
      }}
    >
      <ul className="flex w-max items-center gap-10 sm:gap-16 animate-[ac-marquee_32s_linear_infinite] motion-reduce:animate-none motion-reduce:w-full motion-reduce:flex-wrap motion-reduce:justify-center motion-reduce:gap-8">
        {loop.map((logo, i) => (
          <li key={`${logo.name}-${i}`} className="flex-shrink-0">
            <img
              src={logo.src}
              alt={logo.name}
              className="h-6 sm:h-8 w-auto opacity-50 grayscale transition hover:opacity-100 hover:grayscale-0"
              loading="lazy"
              aria-hidden={i >= FEATURED_LOGOS.length}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── How it works ────────────────────────────────────────────

function HowItWorks({ onStart }: { onStart: () => void }) {
  return (
    <SectionShell
      eyebrow="How it works"
      title="20 minutes to change your next 20 years"
      lede="Over half a million people found their career path with us. Find, pursue, and succeed in your dream careers."
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
        {HOW_IT_WORKS.map((step) => (
          <article key={step.n} className="rounded-3xl border-2 border-ink/10 bg-white p-6 sm:p-7">
            <p className="text-xs font-mono text-ink-light mb-3">{step.n}</p>
            <h3 className="text-lg font-extrabold text-ink leading-tight mb-2">{step.title}</h3>
            <p className="text-sm text-ink-soft leading-relaxed">{step.body}</p>
          </article>
        ))}
      </div>
      <div className="mt-8 text-center">
        <button type="button" className="btn-primary !w-auto !inline-flex" onClick={onStart}>
          Take the assessment →
        </button>
      </div>
    </SectionShell>
  );
}

// ─── Why this test ───────────────────────────────────────────

function WhyThisTest({ onStart }: { onStart: () => void }) {
  return (
    <SectionShell
      eyebrow="The methodology"
      title="Built on CAPBOI — a modern alternative to 40-year-old career tests."
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        <div className="lg:col-span-7 space-y-4 text-ink leading-relaxed">
          <p>
            The standard career tests in circulation today were designed in the 1970s for a workforce that
            doesn't exist anymore. They sort you into a handful of buckets and call it a day. They were
            useful for their time — they're showing their age.
          </p>
          <p>
            We built CAPBOI to map the six traits that actually predict where you'll thrive at work:{' '}
            <strong>Communal</strong>, <strong>Analytical</strong>, <strong>Pragmatic</strong>,{' '}
            <strong>Bold</strong>, <strong>Orthodox</strong>, and <strong>Imaginative</strong>. No score is
            "good" or "bad" — they describe where you'll feel at home and where you'll want backup.
          </p>
          <p>
            Your dominant traits point to the careers and environments that fit you naturally. The quieter
            ones tell you where complementary teammates make the biggest difference. The report grounds
            every recommendation in your actual answers — not a one-size-fits-all template.
          </p>
        </div>
        <div className="lg:col-span-5">
          <div className="rounded-3xl border-2 border-ink/10 bg-canvas-tile p-6 sm:p-8">
            <p className="eyebrow mb-3">Six dimensions</p>
            <ul className="space-y-3 text-sm text-ink">
              {[
                ['C', 'Communal', 'Empathetic, cooperative, energized by team work.'],
                ['A', 'Analytical', 'Logical, detail-oriented, satisfied by problem-solving.'],
                ['P', 'Pragmatic', 'Practical, adaptable, calm when others panic.'],
                ['B', 'Bold', 'Confident, persuasive, comfortable with risk.'],
                ['O', 'Orthodox', 'Structured, disciplined, the long-game scaler.'],
                ['I', 'Imaginative', 'Creative, curious, the around-corners thinker.'],
              ].map(([letter, name, desc]) => (
                <li key={letter} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-md bg-ink text-brand text-xs font-extrabold flex items-center justify-center">
                    {letter}
                  </span>
                  <div className="min-w-0">
                    <p className="font-extrabold text-ink leading-tight">{name}</p>
                    <p className="text-ink-soft leading-snug">{desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className="mt-8 text-center">
        <button type="button" className="btn-primary !w-auto !inline-flex" onClick={onStart}>
          Find Your Dream Careers →
        </button>
      </div>
    </SectionShell>
  );
}

// ─── Types of tests ──────────────────────────────────────────

function TypesOfTests({ onStart }: { onStart: () => void }) {
  return (
    <SectionShell
      eyebrow="Test landscape"
      title="There are four kinds of career tests. We do all four."
      lede="Most products only measure one dimension. The result is a report that sounds confident but misses what actually matters."
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {TYPES.map((t) => (
          <article key={t.name} className="rounded-3xl border-2 border-ink/10 bg-white p-5 sm:p-6">
            <p className="eyebrow mb-2">{t.name}</p>
            <p className="text-sm text-ink leading-relaxed">{t.what}</p>
          </article>
        ))}
      </div>
      <div className="mt-8 text-center">
        <button type="button" className="btn-primary !w-auto !inline-flex" onClick={onStart}>
          Take the Test →
        </button>
      </div>
    </SectionShell>
  );
}

// ─── Who it's for ────────────────────────────────────────────

function WhoFor({ onStart }: { onStart: () => void }) {
  return (
    <SectionShell eyebrow="Who this is for" title="Built for the people most career tests miss.">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {AUDIENCES.map((a) => (
          <article key={a.label} className="rounded-3xl border-2 border-ink/10 bg-white p-6">
            <h3 className="text-lg font-extrabold text-ink mb-2">{a.label}</h3>
            <p className="text-sm text-ink-soft leading-relaxed">{a.body}</p>
          </article>
        ))}
      </div>
      <div className="mt-8 text-center">
        <button type="button" className="btn-primary !w-auto !inline-flex" onClick={onStart}>
          Find Your Calling →
        </button>
      </div>
    </SectionShell>
  );
}

// ─── Comparison ──────────────────────────────────────────────

function Comparison({ onStart }: { onStart: () => void }) {
  return (
    <SectionShell
      eyebrow="What you actually get"
      title="JobTest vs. the typical career test."
    >
      <div className="rounded-card border-2 border-ink/10 bg-white overflow-hidden">
        <div className="grid grid-cols-3 bg-canvas-tile border-b-2 border-ink/10">
          <div className="px-4 sm:px-6 py-4 text-xs font-bold uppercase tracking-wider text-ink-soft">Feature</div>
          <div className="px-4 sm:px-6 py-4 text-xs font-bold uppercase tracking-wider text-brand-700 border-l border-ink/10">JobTest</div>
          <div className="px-4 sm:px-6 py-4 text-xs font-bold uppercase tracking-wider text-ink-soft border-l border-ink/10">Typical test</div>
        </div>
        {COMPARISON.map((row, i) => (
          <div
            key={row.feature}
            className={`grid grid-cols-3 ${i < COMPARISON.length - 1 ? 'border-b border-ink/10' : ''}`}
          >
            <div className="px-4 sm:px-6 py-4 text-sm font-bold text-ink">{row.feature}</div>
            <div className="px-4 sm:px-6 py-4 text-sm text-ink border-l border-ink/10">
              <Check className="text-teal-600 mb-1.5" />
              <span>{row.us}</span>
            </div>
            <div className="px-4 sm:px-6 py-4 text-sm text-ink-soft border-l border-ink/10">{row.them}</div>
          </div>
        ))}
      </div>
      <div className="mt-8 text-center">
        <button type="button" className="btn-primary !w-auto !inline-flex" onClick={onStart}>
          Start mine →
        </button>
      </div>
    </SectionShell>
  );
}

// ─── Impact stats ────────────────────────────────────────────

function ImpactStats({ onStart }: { onStart: () => void }) {
  return (
    <SectionShell eyebrow="Proven impact" title="Used by people taking their next move seriously.">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { num: '600,000+', label: 'Reports generated' },
          { num: '300,000+', label: 'Found their calling' },
          { num: '1,000+', label: 'Hours of coaching delivered' },
        ].map((s) => (
          <article key={s.label} className="rounded-3xl border-2 border-ink/10 bg-white p-6 sm:p-8 text-center">
            <p className="text-display-md text-ink leading-none mb-2 tabular-nums">{s.num}</p>
            <p className="text-sm font-semibold text-ink-soft">{s.label}</p>
          </article>
        ))}
      </div>
      <div className="mt-8 text-center">
        <button type="button" className="btn-primary !w-auto !inline-flex" onClick={onStart}>
          Check Your Income Potential →
        </button>
      </div>
    </SectionShell>
  );
}

// ─── Testimonials ────────────────────────────────────────────

function Testimonials() {
  const picks = REVIEWS.slice(0, 4);
  return (
    <SectionShell
      eyebrow="What people are saying"
      title={`${REVIEW_STATS.averageRating} stars from real users on Trustpilot.`}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
        {picks.map((r) => (
          <article key={r.id} className="rounded-3xl border-2 border-ink/10 bg-white p-6">
            <div className="flex items-center gap-1 mb-3" aria-label={`${r.rating} stars`}>
              {Array.from({ length: r.rating }).map((_, i) => (
                <span key={i} aria-hidden className="text-brand">★</span>
              ))}
            </div>
            <h3 className="text-base font-extrabold text-ink mb-2">{r.title}</h3>
            <p className="text-sm text-ink-soft leading-relaxed mb-3 text-pretty">
              {r.body.length > 240 ? r.body.slice(0, 240).trimEnd() + '…' : r.body}
            </p>
            <p className="text-xs font-semibold text-ink">
              {r.author}
              {r.verified && <span className="text-ink-light font-normal"> · Verified</span>}
            </p>
          </article>
        ))}
      </div>
    </SectionShell>
  );
}

// ─── Coaches preview ─────────────────────────────────────────

function CoachesPreview({ onStart }: { onStart: () => void }) {
  return (
    <SectionShell
      eyebrow="The coaching team"
      title="Real humans on the other side of every report."
      lede="Every paid plan includes access to our coaches — career coaches, recruiters, and talent strategists who have placed thousands into roles they love."
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {COACHES.map((c) => (
          <article key={c.id} className="rounded-2xl border-2 border-ink/10 bg-white p-4 flex flex-col items-center text-center">
            <img
              src={c.imageSrc}
              alt={c.name}
              className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full object-cover border-2 border-ink/10 mb-3"
              loading="lazy"
              width={72}
              height={72}
            />
            <h3 className="text-sm font-extrabold text-ink leading-tight">{c.name}</h3>
            <p className="text-xs text-ink-soft mb-2">{c.title}</p>
            {c.rating != null && c.clientsServed && (
              <div className="flex items-center justify-center gap-1.5 text-xs font-semibold text-ink-soft">
                <span aria-hidden className="text-brand">★</span>
                <span className="tabular-nums text-ink">{c.rating.toFixed(2)}</span>
                <span className="text-ink-light">·</span>
                <span className="tabular-nums">{c.clientsServed}</span>
              </div>
            )}
          </article>
        ))}
      </div>
      <div className="mt-8 text-center">
        <button type="button" className="btn-primary !w-auto !inline-flex" onClick={onStart}>
          Find Your Calling →
        </button>
      </div>
    </SectionShell>
  );
}

// ─── FAQ ─────────────────────────────────────────────────────

function FAQSection() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <SectionShell eyebrow="FAQ" title="Common questions, answered straight.">
      <div className="max-w-3xl mx-auto rounded-card border-2 border-ink/10 bg-white overflow-hidden">
        {FAQ.map((item, i) => {
          const isOpen = open === i;
          return (
            <div key={item.q} className={i < FAQ.length - 1 ? 'border-b border-ink/10' : ''}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : i)}
                className="w-full text-left px-5 sm:px-7 py-5 flex items-start justify-between gap-4 hover:bg-canvas-subtle transition"
                aria-expanded={isOpen}
              >
                <span className="text-base font-extrabold text-ink leading-snug">{item.q}</span>
                <span
                  aria-hidden
                  className={`flex-shrink-0 w-6 h-6 rounded-full bg-canvas-subtle text-ink-soft font-bold flex items-center justify-center transition-transform ${
                    isOpen ? 'rotate-45' : ''
                  }`}
                >
                  +
                </span>
              </button>
              {isOpen && (
                <div className="px-5 sm:px-7 pb-5 -mt-1">
                  <p className="text-sm text-ink-soft leading-relaxed text-pretty">{item.a}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </SectionShell>
  );
}

// ─── Final CTA ───────────────────────────────────────────────

function FinalCTA({ ctaLabel, onStart }: { ctaLabel: string; onStart: () => void }) {
  return (
    <section className="max-w-[1200px] mx-auto px-4 sm:px-8 pb-16 sm:pb-24">
      <div className="rounded-card border-2 border-ink bg-brand-50 p-8 sm:p-14 text-center shadow-[6px_6px_0_0_rgba(15,18,23,1)]">
        <p className="eyebrow mb-3">Ready when you are</p>
        <h2 className="text-display-md sm:text-display-lg text-ink leading-tight mb-4 max-w-2xl mx-auto text-balance">
          Start today. A real plan by tonight.
        </h2>
        <p className="text-ink-soft text-base sm:text-lg max-w-xl mx-auto mb-7">
          Free to take. Pay only if you want the full report and coaching access.
        </p>
        <button type="button" className="btn-primary !w-auto !inline-flex" onClick={onStart}>
          {ctaLabel}
        </button>
      </div>
    </section>
  );
}

// ─── Footer ──────────────────────────────────────────────────

function PageFooter() {
  return (
    <footer className="border-t border-ink/10 bg-canvas-subtle">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-10 grid grid-cols-1 sm:grid-cols-2 gap-5 items-center">
        <div>
          <img src="/jobtestlogo.svg" alt="Jobtest.org" className="h-6 w-auto mb-2" />
          <p className="text-xs text-ink-soft">Modern career assessment built on the CAPBOI personality model.</p>
        </div>
        <div className="flex flex-wrap gap-5 sm:justify-end text-sm font-semibold text-ink-soft">
          <a href="mailto:hello@jobtest.org" className="hover:text-ink transition">Contact</a>
          <a href="/login" className="hover:text-ink transition">Sign in</a>
        </div>
      </div>
    </footer>
  );
}

// ─── Section shell ───────────────────────────────────────────

function SectionShell({
  eyebrow,
  title,
  lede,
  children,
}: {
  eyebrow: string;
  title: string;
  lede?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="max-w-[1200px] mx-auto px-4 sm:px-8 py-12 sm:py-20 border-t border-ink/10">
      <header className="max-w-3xl mb-8 sm:mb-10">
        <p className="eyebrow mb-3">{eyebrow}</p>
        <h2 className="text-display-md text-ink leading-[1.05] mb-3 text-balance">{title}</h2>
        {lede && <p className="text-ink-soft text-base sm:text-lg leading-relaxed text-pretty">{lede}</p>}
      </header>
      {children}
    </section>
  );
}

// ─── Icons ───────────────────────────────────────────────────

function Check({ className = '' }: { className?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden className={className}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
